import type { TokenStore } from '@/lib/api-client';
import type { AuthTokens } from '@/lib/shared-types';

const STORAGE_KEY = 'venue.tokens';

/**
 * The tokens, in their own module.
 *
 * They used to live in `api.ts`, which was fine until the socket layer had to
 * be torn down from the HTTP client's 401 path: `api.ts` needs
 * `disconnectSocket` from `lib/realtime.ts`, and `lib/realtime.ts` needs the
 * tokens. Importing both directions is a cycle whose evaluation order depends on
 * which screen happens to be imported first — the kind of thing that works in
 * dev and produces an undefined `tokenStore` in one bundle chunk. The tokens
 * depend on nothing, so they move down here and both sides import them.
 *
 * `api.ts` still re-exports it, so every existing `import { tokenStore } from
 * '@/lib/api'` — and every test that mocks that module — keeps working.
 */
export const tokenStore: TokenStore = {
  get() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as AuthTokens) : null;
    } catch {
      return null;
    }
  },
  set(tokens) {
    if (tokens) localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
    else localStorage.removeItem(STORAGE_KEY);
  },
};
